import { type FC, useCallback, useMemo, useState } from "react";

import { Group } from "@mantine/core";
import { Button } from "@snapchat/snap-design-system";
import { EllipsisIcon } from "@snapchat/snap-design-system-icons";

import { type RewardedLens } from "../../../../gql-generated/types";
import { MeatBallsMenu } from "../../../../lens/components/shared/meatballs-menu/MeatBallsMenu";
import { useLensQrCode } from "../../../../lens/hooks/useLensQrCode";
import { type LensActionsVisibilityState, hiddenShowWithDisabledConfig } from "../../../../lens/state";
import { getLensScanCode } from "../../../../lens/utils/getLensScanCode";
import { CopyDeeplink } from "../../../../lenses/components/list/row/CopyDeeplink";
import { InsightsRoundButton } from "../../../../lenses/components/list/row/InsightsRoundButton";
import { copyToClipboard } from "../../../../shared/utils/browser";

interface RewardedLensActionsProps {
    row: RewardedLens;
}

const MEATBALLS_ACTIONS_CONFIG: LensActionsVisibilityState = {
    downloadQrCode: true,
    deleteLens: true,
    setTags: false,
    setVisibility: false,
    setLensCta: false,
    setLensCategory: false,
    setPreview: false,
    manageDigitalGoods: false,
    finalize: false,
    copyDeeplink: false,
    publishToSnapchatConfig: hiddenShowWithDisabledConfig,
    enrollCreatorPayout: hiddenShowWithDisabledConfig,
    setLensDescription: false,
    deleteHostedWebAr: false,
    generateHostedWebAr: false,
};

export const RewardedLensActions: FC<RewardedLensActionsProps> = ({ row }) => {
    const lens = row.lens;
    const [menuVisible, setMenuVisible] = useState(false);
    const { qrCode, loading, error, refetch } = useLensQrCode(lens?.id, menuVisible && Boolean(lens?.qrCode?.deeplink));
    const scanCode = useMemo(() => getLensScanCode(qrCode), [qrCode]);
    const deeplink = lens?.qrCode?.deeplink;

    const retryQrCode = useCallback(() => {
        void refetch().catch(() => {
            // The query's error state renders retry feedback in the menu.
        });
    }, [refetch]);

    const copyDeeplinkToClipboard = useCallback(() => {
        if (deeplink) {
            copyToClipboard(deeplink);
        }
    }, [deeplink]);

    if (!lens) return null;

    return (
        <Group
            gap={4}
            wrap="nowrap"
            onClick={(e) => {
                e.stopPropagation();
            }}
            style={{ transform: "scale(0.85)" }}
        >
            <CopyDeeplink active={Boolean(deeplink)} copyDeeplinkToClipboard={copyDeeplinkToClipboard} />
            {lens.lensAccountId?.lensSpaceId && (
                <InsightsRoundButton
                    lensId={lens.id}
                    orgId={lens.organizationId}
                    lensFolderId={lens.lensAccountId.lensSpaceId}
                    isDisabled={false}
                />
            )}
            <MeatBallsMenu
                lensId={lens.id}
                lensCreatorPayoutEligibility={lens.lensCreatorPayoutEligibility}
                scanCode={qrCode?.deeplink && deeplink ? scanCode : undefined}
                onVisibleChange={setMenuVisible}
                scanCodeLoading={loading}
                scanCodeError={Boolean(error)}
                onRetryScanCode={retryQrCode}
                actionsConfig={MEATBALLS_ACTIONS_CONFIG}
            >
                <Button shape="circle">
                    <EllipsisIcon />
                </Button>
            </MeatBallsMenu>
        </Group>
    );
};
